<template>
  <section id="batch-list">
    <nav class="columns mb-1">
      <div class="column">
        <div class="buttons">
          <b-button class="is-info" @click="selectingProduct">
            {{ $t('attributes.select') + $t('attributes.product') }}
          </b-button>
          <b-button type="is-text" @click="toggleAdvance">
            {{ $t('attributes.advance_search') }}
          </b-button>
        </div>
      </div>
      <div class="column is-narrow">
        <div style="width: 200px">
          <b-button type="is-success" expanded @click="onSave">
            {{ $t('attributes.save') }}
          </b-button>
        </div>
      </div>
    </nav>

    <nav v-show="advanceSearch" class="columns mb-1">
      <div class="column">
        <b-field grouped group-multiline>
          <b-select
            v-model="selectedArea"
            :loading="loadingAreas"
            :placeholder="$t('message.please_select') + $t('attributes.area')"
            @input="onSelectArea"
          >
            <option value="" disabled>
              {{ $t('message.please_select') + $t('attributes.area') }}
            </option>
            <option v-for="option in areas" :value="option" :key="option.code">
              {{ option.name }}
            </option>
          </b-select>

          <b-select
            v-model="selectedCatalog"
            :loading="loadingAreas"
            :disabled="catalogs.length <= 0"
            :placeholder="
              $t('message.please_select') + $t('attributes.catalog')
            "
            @input="onSelectCatalog"
          >
            <option value="">全部類別</option>
            <option
              v-for="option in catalogs"
              :value="option"
              :key="option.catalog_no"
            >
              {{ option.name }}
            </option>
          </b-select>

          <b-field
            :label="$t('attributes.activation_status')"
            label-position="on-border"
            class="mb-3"
          >
            <b-select v-model="filterStatus">
              <option value="all">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.shelve_on') }}</option>
              <option value="false">{{ $t('attributes.shelve_off') }}</option>
            </b-select>
          </b-field>

          <b-field
            :label="$t('attributes.travel_group_type')"
            label-position="on-border"
            class="mb-3"
          >
            <b-select v-model="filterType">
              <option value="all">{{ $t('attributes.all') }}</option>
              <option
                v-for="(type, idx) in groupTypes"
                :key="idx"
                :value="type.value"
              >
                {{ type.name }}
              </option>
            </b-select>
          </b-field>

          <b-field
            :label="$t('attributes.is_promote')"
            label-position="on-border"
            style="width: 100px"
            class="mb-3"
          >
            <b-select v-model="isHome" expanded>
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.yes') }}</option>
              <option value="false">{{ $t('attributes.no') }}</option>
            </b-select>
          </b-field>

          <b-select v-model="showExpired">
            <option value="true">
              {{ $t('attributes.display_expired') }}
            </option>
            <option value="false">
              {{ $t('attributes.not_display_expired') }}
            </option>
          </b-select>

          <b-datepicker
            :placeholder="
              $t('message.please_select') + $t('attributes.departure_date')
            "
            v-model="selectedDates"
            range
            @input="onSelectedDates"
          >
            <button class="button is-danger" @click="onClearDates">
              <b-icon icon="close"></b-icon>
              <span>Clear</span>
            </button>
          </b-datepicker>

          <b-input
            v-model="searchCode"
            :placeholder="$t('attributes.search') + $t('attributes.group_code')"
            type="search"
            icon="magnify"
          ></b-input>

          <b-input
            v-model="searchName"
            :placeholder="$t('attributes.search') + $t('attributes.name')"
            type="search"
            icon="magnify"
          ></b-input>

          <div class="buttons mb-3">
            <b-button :loading="sending" type="is-info" @click="onSearch">
              {{ $t('attributes.search') }}
            </b-button>
            <b-button
              :loading="sending"
              type="is-primary"
              outlined
              @click="onClear"
            >
              {{ $t('attributes.reset') }}
            </b-button>
          </div>
        </b-field>
      </div>
    </nav>

    <b-tabs
      v-model="activeTab"
      type="is-boxed"
      class="mb-2"
      @input="onChangeTab"
    >
      <b-tab-item :label="$t('attributes.group_name')">
        <batch-name-table
          ref="nameTable"
          :groups="items"
          :height="tableHeight"
          :loading="loadingItems"
        />
      </b-tab-item>

      <b-tab-item :label="$t('attributes.detail_info')">
        <batch-basic-table
          ref="basicTable"
          :groups="items"
          :airlines="airlines"
          :situation-types="situationTypes"
          :height="tableHeight"
          :loading="loadingItems"
        />
      </b-tab-item>

      <b-tab-item label="售價設定">
        <batch-price-table
          ref="priceTable"
          :groups="items"
          :height="tableHeight"
          :loading="loadingItems"
        />
      </b-tab-item>

      <b-tab-item label="售價進階(方案)">
        <batch-plan-table
          ref="planTable"
          :groups="items"
          :height="tableHeight"
          :loading="loadingItems"
        >
        </batch-plan-table>
      </b-tab-item>

      <b-tab-item label="航班交通">
        <batch-transfer-table
          ref="transferTable"
          :groups="items"
          :height="tableHeight"
          :loading="loadingItems"
        >
        </batch-transfer-table>
      </b-tab-item>

      <b-tab-item label="國旗">
        <batch-country-table
          ref="countryTable"
          :groups="items"
          :height="tableHeight"
          :loading="loadingItems"
        />
      </b-tab-item>

      <b-tab-item label="城市氣象">
        <batch-weather-table
          ref="weatherTable"
          :groups="items"
          :height="tableHeight"
          :loading="loadingItems"
        />
      </b-tab-item>
    </b-tabs>

    <b-modal
      :active.sync="isSelectModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
      :width="1200"
      scroll="keep"
    >
      <product-selector @selected="onSelectedProduct" />
    </b-modal>

    <b-loading :active="isLoading" is-full-page :can-cancel="false"></b-loading>
  </section>
</template>

<script>
import { AIRLINE_API, AREA_API, TRAVEL_GROUPS_BATCH_API } from '@/utils/apis'
import ProductSelector from '@/components/travel_groups/ProductSelector'
import BatchNameTable from '@/components/travel_groups/BatchNameTable'
import BatchBasicTable from '@/components/travel_groups/BatchBasicTable'
import BatchPriceTable from '@/components/travel_groups/BatchPriceTable'
import BatchPlanTable from '@/components/travel_groups/BatchPlanTable'
import BatchTransferTable from '@/components/travel_groups/BatchTransferTable'
import BatchCountryTable from '@/components/travel_groups/BatchCountryTable'
import BatchWeatherTable from '@/components/travel_groups/BatchWeatherTable'
import notifyService from '@/services/notifyService'

export default {
  name: 'TravelGroupBatchList',
  components: {
    ProductSelector,
    BatchNameTable,
    BatchBasicTable,
    BatchPriceTable,
    BatchTransferTable,
    BatchCountryTable,
    BatchWeatherTable,
    BatchPlanTable
  },

  data() {
    const { query } = this.$route
    let startDate = null
    let endDate = null
    let selectedDates = []

    const groupTypes = this.GetTravelGroupTypes()

    if (query.start && query.end) {
      startDate = this.$dayjs.unix(query.start).toDate()
      endDate = this.$dayjs.unix(query.end).toDate()
      selectedDates = [startDate, endDate]
    }

    return {
      prevTab: 0,
      activeTab: 0,
      advanceSearch: false,

      groupTypes,
      loading: false,
      loadingItems: false,
      total: 0,
      items: [],

      airlines: [],
      situationTypes: [],

      loadingAreas: false,
      areas: [],
      catalogs: [],
      selectedArea: null,
      selectedAreaCode: query.ac || null,
      selectedCatalog: null,
      selectedCatalogNo: query.cn || null,

      filterType: query.type || 'all',
      filterStatus: query.status || 'true',
      showExpired: query.exp || false,
      isHome: query.is_home || '',
      searchCode: query.code || '',
      searchName: query.search || '',

      refresh_count: query.rc || 0,

      selectedDates,
      startDate,
      endDate,

      isSelectModalActive: false,
      selectedProduct: null,

      sending: false,

      windowHeight: window.innerHeight
    }
  },
  computed: {
    isLoading() {
      return this.sending || this.loadingItems
    },
    tableHeight() {
      return this.windowHeight - (this.advanceSearch ? 318 : 210)
    }
  },
  mounted() {
    this.loadAreas()

    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize)
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  beforeRouteUpdate(to, from, next) {
    if (to.name === from.name) {
      this.getQuery(to.query)
      this.loadDate()
    }

    next()
  },
  methods: {
    async loadDate() {
      if (!this.selectedAreaCode) {
        return
      }

      this.loading = true
      await this.loadAirlines()
      await this.loadSituationTypes(this.selectedAreaCode)
      await this.loadTravelGroups()
      this.loading = false
    },
    async loadAreas() {
      await this.loadAreasAsync()

      if (this.selectedAreaCode) {
        await this.loadDate()
      }
    },
    async loadAreasAsync() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.catalogs = []
        this.selectedArea = null
        this.selectedCatalog = null

        if (this.selectedAreaCode) {
          this.selectedArea = areas.find(a => a.code === this.selectedAreaCode)
          this.catalogs = this.selectedArea
            ? this.selectedArea.catalogs.sort(c => c.sort)
            : []
        }
        if (this.selectedCatalogNo) {
          this.selectedCatalog = this.catalogs.find(
            c => c.catalog_no === this.selectedCatalogNo
          )
        }
      } catch (e) {
        this.areas = []
        this.catalogs = []
        this.selectedArea = null
        this.selectedAreaCode = null
        this.selectedCatalog = null
        this.selectedCatalogNo = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async loadAirlines() {
      try {
        const res = await this.axios.get(`${AIRLINE_API}/list`)
        this.airlines = res.data.data.map(air => ({
          ...air,
          code: air.code.trim(),
          name: air.name.trim(),
          english_name: air.english_name ? air.english_name.trim() : '',
          display_name: this.formatAirlineName(air)
        }))
      } catch (e) {
        this.airlines = []

        notifyService.fetch_error('airline', e)
      }
    },
    async loadSituationTypes(areaCode) {
      try {
        const res = await this.axios.get(`${AREA_API}/${areaCode}/events`)
        this.situationTypes = res.data.data
      } catch (e) {
        this.situationTypes = []

        notifyService.fetch_error('travel_group', e)
      }
    },
    async loadTravelGroups() {
      try {
        const api = TRAVEL_GROUPS_BATCH_API

        const params = {
          area_code: this.selectedAreaCode,
          catalog_no: this.selectedCatalogNo,
          type: this.filterType !== 'all' ? this.filterType : null,
          active:
            this.filterStatus !== 'all' ? this.filterStatus === 'true' : null,
          show_expired: this.showExpired,
          is_home: this.isHome,
          start_date: this.startDate,
          end_date: this.endDate,
          search_code: this.searchCode,
          search: this.searchName
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        this.items = res.data.data
        this.total = this.items.length
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('travel_group', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.filterType = query.type || 'all'
      this.filterStatus = query.status || 'true'
      this.showExpired = query.exp || false
      this.isHome = query.is_home || ''
      this.searchCode = query.code || ''
      this.searchName = query.search || ''

      if (query.start && query.end) {
        this.startDate = this.$dayjs.unix(query.start).toDate()
        this.endDate = this.$dayjs.unix(query.end).toDate()
        this.selectedDates = [this.startDate, this.endDate]
      } else {
        this.startDate = null
        this.endDate = null
        this.selectedDates = []
      }

      this.selectedArea = null
      this.selectedCatalog = null
      this.selectedAreaCode = query.ac || null
      this.selectedCatalogNo = query.cn || null

      this.refresh_count = query.rc || null

      if (this.selectedAreaCode) {
        this.selectedArea = this.areas.find(
          a => a.code === this.selectedAreaCode
        )
        this.catalogs = this.selectedArea
          ? this.selectedArea.catalogs.sort(c => c.sort)
          : []
      }
      if (this.selectedCatalogNo) {
        this.selectedCatalog = this.catalogs.find(
          c => c.catalog_no === this.selectedCatalogNo
        )
      }
    },
    updateQuery() {
      const query = {
        ac: this.selectedAreaCode,
        cn: this.selectedCatalogNo,
        type: this.filterType,
        status: this.filterStatus,
        exp: this.showExpired,
        is_home: this.isHome,
        start: this.startDate && this.$dayjs(this.startDate).unix(),
        end: this.endDate && this.$dayjs(this.endDate).unix(),
        code: this.searchCode,
        search: this.searchName,
        rc: parseInt(this.refresh_count) + 1
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },

    onSelectArea(option) {
      this.selectedArea = option
      this.selectedAreaCode = option ? option.code : null
      this.catalogs = option ? option.catalogs.sort(c => c.sort) : []
      this.selectedCatalog = null
      this.selectedCatalogNo = null
    },
    onSelectCatalog(option) {
      this.selectedCatalog = option
      this.selectedCatalogNo = option ? option.catalog_no : null
    },
    onSelectedDates() {
      this.startDate = this.selectedDates[0]
      this.endDate = this.selectedDates[1]
    },
    onClearDates() {
      this.selectedDates = null
      this.startDate = null
      this.endDate = null
    },

    toggleAdvance() {
      this.advanceSearch = !this.advanceSearch
    },
    selectingProduct() {
      if (this.checkDirty()) {
        return
      }

      this.isSelectModalActive = true
    },
    onSelectedProduct(product, area_code, catalog_no) {
      this.isSelectModalActive = false

      this.searchCode = product.code.trim()
      this.selectedProduct = product
      this.selectedAreaCode = area_code
      this.selectedCatalogNo = catalog_no

      this.selectedArea = this.areas.find(a => a.code === this.selectedAreaCode)
      this.catalogs = this.selectedArea
        ? this.selectedArea.catalogs.sort(c => c.sort)
        : []

      this.selectedCatalog = this.catalogs.find(
        c => c.catalog_no === this.selectedCatalogNo
      )

      this.updateQuery()
    },
    onSearch() {
      if (!this.selectedAreaCode) {
        notifyService.error('請選擇線別！')
        return
      }

      if (this.checkDirty()) {
        return
      }

      this.updateQuery()
    },
    onClear() {
      if (this.checkDirty()) {
        return
      }

      this.total = 0
      this.items = []

      this.catalogs = []
      this.selectedArea = null
      this.selectedAreaCode = null
      this.selectedCatalog = null
      this.selectedCatalogNo = null

      this.filterType = 'all'
      this.filterStatus = 'true'
      this.showExpired = false
      this.isHome = ''
      this.searchCode = ''
      this.searchName = ''

      this.refresh_count = 0

      this.startDate = null
      this.endDate = null
      this.selectedDates = []

      this.updateQuery()
    },

    onChangeTab() {
      const table = this.getTable(this.prevTab)

      if (table.isDirty()) {
        notifyService.error('資料未儲存！！')

        setTimeout(() => {
          this.activeTab = this.prevTab
        }, 0)

        return false
      }

      this.prevTab = this.activeTab
    },
    getTable(tabIndex) {
      let table

      switch (tabIndex) {
        case 0:
          table = this.$refs.nameTable
          break
        case 1:
          table = this.$refs.basicTable
          break
        case 2:
          table = this.$refs.priceTable
          break
        case 3:
          table = this.$refs.planTable
          break
        case 4:
          table = this.$refs.transferTable
          break
        case 5:
          table = this.$refs.countryTable
          break
        case 6:
          table = this.$refs.weatherTable
          break
        default:
          table = this.$refs.nameTable
          break
      }

      return table
    },
    getAPI(tabIndex) {
      let api = TRAVEL_GROUPS_BATCH_API

      switch (tabIndex) {
        case 0:
          api += '/names'
          break
        case 1:
          api += '/basic'
          break
        case 2:
          api += '/price'
          break
        case 3:
          api += '/plan'
          break
        case 4:
          api += '/transfer'
          break
        case 5:
          api += '/country'
          break
        case 6:
          api += '/weather'
          break
        default:
          api += '/names'
          break
      }

      return api
    },

    checkDirty() {
      if (this.isDirty()) {
        notifyService.error('資料未儲存！！')
        return true
      }
      if (this.isEditing()) {
        notifyService.error('編輯中，請先完成修改！')
        return true
      }

      return false
    },
    isDirty() {
      const table = this.getTable(this.activeTab)
      return table.isDirty()
    },
    isEditing() {
      const table = this.getTable(this.activeTab)
      return table.isEditing()
    },

    async onSave() {
      const table = this.getTable(this.activeTab)
      const api = this.getAPI(this.activeTab)

      if (table.isEditing()) {
        notifyService.error('編輯中，請先完成修改！')
        return
      }

      if (!table.isDirty()) {
        notifyService.show('未修改任何旅行團！')
        return
      }

      if (!table.isValidate()) {
        notifyService.error('請先修正錯誤！')
        return
      }

      const groups = table.getData()

      try {
        this.sending = true
        const res = await this.axios.put(api, { groups })
        const _items = res.data.data

        const items = [...this.items]
        for (let item of _items) {
          const idx = this.items.findIndex(x => x.code === item.code)
          items[idx] = item
        }
        this.items = items

        notifyService.success(`${this.$t('attributes.update_succeed')}`)
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    onResize() {
      this.windowHeight = window.innerHeight
    }
  }
}
</script>

<style lang="scss">
#batch-list {
  .datepicker {
    .datepicker-table {
      .datepicker-cell {
        padding: 0.6rem 0.65rem;
      }
    }
  }

  .b-tabs .tab-content {
    padding: 12px 8px 0;
  }

  & > .table-wrapper {
    & > .table {
      min-width: 1024px;
    }
  }
}
</style>
